/**
 * HomeAura.jsx — landing-page ambience + FOMO widget
 * ==================================================
 * Two exports:
 *   1. <GoldDustParticles />      — CSS-only floating gold particles
 *   2. <PopularNowWidget />       — 4-game "Live Playing" ticker
 *
 * Performance notes
 * -----------------
 * - Particles use `transform` + `will-change: transform` so the whole
 *   animation stays on the compositor thread. NEVER animate top/left.
 * - Particle count is capped at 30 (18 on <768px) — enough for the aura
 *   effect, cheap enough for iPhone 15 mid-range GPUs.
 * - Widget uses a single `setInterval(5000)` that mutates a `useRef` map
 *   plus one re-render — no per-particle React state.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";

// ─── 1. GOLD-DUST PARTICLE BACKGROUND ──────────────────────────────
export function GoldDustParticles({ count = 30 }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const N = isMobile ? Math.min(count, 18) : count;

  // Randomise once — memoised so the particles don't reshuffle on every render.
  const particles = useMemo(
    () =>
      Array.from({ length: N }, (_, i) => ({
        id: i,
        left: Math.random() * 100,                  // %
        size: 2 + Math.random() * 4,                // px  (2-6px)
        duration: 14 + Math.random() * 18,          // s   (14-32s)
        delay: -Math.random() * 20,                 // s   (start mid-cycle)
        drift: (Math.random() - 0.5) * 40,          // px  side-to-side sway
      })),
    [N]
  );

  return (
    <div
      aria-hidden="true"
      data-testid="gold-dust-aura"
      className="pointer-events-none fixed inset-0 overflow-hidden z-0"
      style={{ contain: "strict" }}
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className="gold-dust-particle"
          style={{
            left:                    `${p.left}%`,
            width:                   `${p.size}px`,
            height:                  `${p.size}px`,
            animationDuration:       `${p.duration}s`,
            animationDelay:          `${p.delay}s`,
            "--drift":               `${p.drift}px`,
          }}
        />
      ))}
      <style>{`
        .gold-dust-particle {
          position: absolute;
          bottom: -10px;
          border-radius: 9999px;
          background: radial-gradient(circle, rgba(251,191,36,0.7) 0%, rgba(251,191,36,0.15) 60%, transparent 100%);
          opacity: 0.5;
          filter: blur(0.5px);
          will-change: transform, opacity;
          animation-name: gold-dust-rise;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          transform: translate3d(0, 0, 0);
        }
        @keyframes gold-dust-rise {
          0%   { transform: translate3d(0, 0, 0)                  scale(1);   opacity: 0;   }
          10%  { opacity: 0.5; }
          50%  { transform: translate3d(var(--drift), -50vh, 0)   scale(1.1); opacity: 0.5; }
          90%  { opacity: 0.5; }
          100% { transform: translate3d(0, -110vh, 0)             scale(0.9); opacity: 0;   }
        }
        @media (prefers-reduced-motion: reduce) {
          .gold-dust-particle { animation: none; opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}


// ─── 2. POPULAR RIGHT NOW / LIVE-PLAYING WIDGET ────────────────────
// Curated 4 titles + shipped thumbnails. Uses backend static asset routes
// where available; falls back to a public thumb if the file isn't shipped.
// ── All thumbnails point at real provider CDN routes on the JackRoyals
// backend. Fallback stays live so a proxy hiccup won't break the tile.
// Ocean King (Aug 2026) was swapped for a genuine KA Gaming shooter
// (Fishing Expedition) per product ask.
const POPULAR_GAMES = [
  {
    id:        "ka_fishing_expedition",
    name:      "Fishing Expedition",
    provider:  "KA Gaming",
    thumb:     "/api/ka/thumb/FishingExpedition",
    fallback:  "/api/ka/thumb/KAFishHunter",
    base:      210,
  },
  {
    id:        "petersons_big_bounty_bandits",
    name:      "Big Bounty Bandits 3 Pots",
    provider:  "Redwood",
    thumb:     "/api/game-assets/petersons/big-bounty-bandits.png",
    fallback:  "/api/game-assets/petersons/bloody-mary.png",
    base:      265,
  },
  {
    id:        "boom_lucky_charms_trio_10000",
    name:      "Lucky Charms Trio 10,000",
    provider:  "Booming",
    thumb:     "/api/game-assets/booming/lucky-charms-trio-10000.png",
    fallback:  "/api/game-assets/petersons/bear-patrol.png",
    base:      182,
  },
  {
    id:        "betsoft_the_game_show",
    name:      "The Game Show",
    provider:  "Betsoft",
    thumb:     "/games/betsoft/the-game-show-500x500.jpg",
    fallback:  "/api/game-assets/petersons/greedy-alice-2.png",
    base:      241,
  },
];

export function PopularNowWidget({ onPlay }) {
  // Seed each counter to a random base between 150-290 on mount.
  const [counts, setCounts] = useState(() =>
    POPULAR_GAMES.reduce((acc, g) => {
      acc[g.id] = 150 + Math.floor(Math.random() * 141); // 150..290 inclusive
      return acc;
    }, {})
  );
  const [tick, setTick] = useState(0);
  const countsRef = useRef(counts);
  countsRef.current = counts;

  // Drift each counter ±1-3 every 5s. Clamp to a wider [140, 310] range so
  // the numbers stay in the "popular" band instead of eventually drifting
  // into a dead zone.
  useEffect(() => {
    const iv = setInterval(() => {
      const next = { ...countsRef.current };
      for (const g of POPULAR_GAMES) {
        const delta = (Math.floor(Math.random() * 3) + 1) * (Math.random() < 0.5 ? -1 : 1); // ±1-3
        next[g.id] = Math.max(140, Math.min(310, (next[g.id] || 200) + delta));
      }
      setCounts(next);
      setTick((t) => t + 1);
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  // Rank the games by live count so the HOT badge lands on the top-3 dynamically.
  const rankedIds = useMemo(() => {
    return [...POPULAR_GAMES]
      .sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0))
      .slice(0, 3)
      .map((g) => g.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  return (
    <section
      data-testid="popular-now-widget"
      className="relative py-14 md:py-20 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="text-[10px] sm:text-xs font-black tracking-[0.4em] uppercase bg-gradient-to-r from-rose-300 via-amber-300 to-cyan-300 bg-clip-text text-transparent">
            🔥 LIVE FOMO · UPDATED EVERY 5 SECONDS 🔥
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mt-3" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Popular <span className="bg-gradient-to-r from-rose-400 via-amber-300 to-rose-400 bg-clip-text text-transparent">Right Now</span>
          </h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm sm:text-base">
            The hottest tables on JackRoyals in the last 5 seconds. Jump in before the queue grows.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {POPULAR_GAMES.map((g, idx) => {
            const isHot = rankedIds.includes(g.id);
            const hotRank = rankedIds.indexOf(g.id) + 1;
            return (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: idx * 0.06, type: "spring", stiffness: 120, damping: 18 }}
                whileHover={{ y: -6 }}
                className="group relative rounded-2xl overflow-hidden border border-white/[0.06] bg-gradient-to-br from-slate-900/95 to-slate-950 hover:border-amber-400/50 transition-all duration-300 shadow-[0_10px_40px_rgba(0,0,0,0.5)] hover:shadow-[0_10px_50px_rgba(251,191,36,0.25)]"
                data-testid={`popular-tile-${g.id}`}
              >
                {isHot && (
                  <div
                    className="absolute top-2 right-2 z-30 flex items-center gap-1 px-2 py-0.5 rounded-md bg-gradient-to-r from-rose-500 to-amber-500 text-white text-[9px] sm:text-[10px] font-black tracking-[0.14em] shadow-[0_0_18px_rgba(244,63,94,0.6)] animate-pulse"
                    data-testid={`hot-badge-${g.id}`}
                    style={{ fontFamily: "'Unbounded', sans-serif" }}
                  >
                    <span aria-hidden>🔥</span> HOT #{hotRank}
                  </div>
                )}

                <div className="absolute top-2 left-2 z-20">
                  <span className="px-1.5 py-0.5 rounded-md bg-black/60 border border-white/10 text-[9px] font-black tracking-[0.12em] backdrop-blur-sm text-slate-200" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                    {g.provider}
                  </span>
                </div>

                <div className="relative aspect-square overflow-hidden bg-black">
                  <img
                    src={g.thumb}
                    onError={(e) => { e.currentTarget.src = g.fallback; }}
                    alt={g.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                </div>

                <div className="relative px-3 py-3 flex flex-col gap-2">
                  <p className="text-sm font-black text-white truncate" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                    {g.name}
                  </p>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-mono">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                      </span>
                      <span className="text-emerald-300 font-black tabular-nums" data-testid={`live-count-${g.id}`}>
                        {counts[g.id] || 0}
                      </span>
                      <span className="text-slate-500 uppercase tracking-wider text-[9px]">playing</span>
                    </div>
                  </div>

                  <button
                    onClick={() => onPlay?.(g)}
                    data-testid={`popular-play-btn-${g.id}`}
                    className="mt-1 w-full py-2.5 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-slate-950 font-black text-xs tracking-[0.18em] uppercase shadow-[0_0_18px_rgba(251,191,36,0.5)] hover:shadow-[0_0_28px_rgba(251,191,36,0.75)] transition-all animate-pulse-play"
                    style={{ fontFamily: "'Unbounded', sans-serif" }}
                  >
                    ▶ PLAY
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes pulse-play {
          0%, 100% { transform: scale(1);    box-shadow: 0 0 18px rgba(251,191,36,0.5); }
          50%      { transform: scale(1.03); box-shadow: 0 0 30px rgba(251,191,36,0.8); }
        }
        .animate-pulse-play { animation: pulse-play 1.8s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .animate-pulse-play { animation: none; }
        }
      `}</style>
    </section>
  );
}
